import type { CaptureData } from './types.js';

const CSSOM_SIZE_CAP = 2_000_000; // 2 MB cap on inlined CSS
const URL_ATTRIBUTES = ['href', 'poster'] as const;
const SRC_ATTRIBUTES = ['src'] as const;
const SRCSET_ATTRIBUTES = ['srcset'] as const;

/**
 * Compresses an HTML string with gzip via the native CompressionStream API
 * and returns a base64-encoded string — same scheme as iframe-utils.js.
 */
async function compressHtmlToBase64(htmlStr: string): Promise<string> {
  const encoder = new TextEncoder();
  const bytes = encoder.encode(htmlStr);
  const stream = new ReadableStream({
    start(controller) {
      controller.enqueue(bytes);
      controller.close();
    },
  });
  const compressedStream = stream.pipeThrough(new CompressionStream('gzip'));
  const reader = compressedStream.getReader();
  const chunks: Uint8Array[] = [];
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
  }
  const totalLength = chunks.reduce((sum, c) => sum + c.length, 0);
  const merged = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    merged.set(chunk, offset);
    offset += chunk.length;
  }
  // Spread-as-args crashes on large buffers (RangeError: Maximum call stack
  // size exceeded). Convert in chunks to avoid the argument-count limit.
  const CHUNK = 8192;
  let binaryStr = '';
  for (let i = 0; i < merged.length; i += CHUNK) {
    binaryStr += String.fromCharCode(...merged.subarray(i, i + CHUNK));
  }
  return btoa(binaryStr);
}

/**
 * Inlines computed (runtime) CSS from all accessible stylesheets.
 * Cross-origin sheets are silently skipped.
 */
function collectRuntimeCss(): string {
  let css = '';
  let totalSize = 0;
  try {
    for (const sheet of Array.from(document.styleSheets)) {
      try {
        const rules = Array.from(sheet.cssRules ?? []);
        for (const rule of rules) {
          const text = rule.cssText;
          totalSize += text.length;
          if (totalSize > CSSOM_SIZE_CAP) return css;
          css += text + '\n';
        }
      } catch {
        // cross-origin sheet — skip
      }
    }
  } catch {
    // styleSheets enumeration failed
  }
  return css;
}

function resolveUrl(value: string): string {
  if (!value || value.startsWith('data:') || value.startsWith('blob:')) return value;

  try {
    return new URL(value, window.location.href).href;
  } catch {
    return value;
  }
}

function resolveSrcset(value: string): string {
  if (!value || value.includes('data:')) return value;

  return value
    .split(',')
    .map((candidate) => {
      const parts = candidate.trim().split(/\s+/);
      const url = parts.shift();
      return url ? [resolveUrl(url), ...parts].join(' ') : candidate;
    })
    .join(', ');
}

function normalizeClonedAssetUrls(clone: HTMLElement): void {
  const liveImages = Array.from(document.querySelectorAll('img'));
  const clonedImages = Array.from(clone.querySelectorAll('img'));

  liveImages.forEach((liveElement, index) => {
    const clonedElement = clonedImages[index];
    if (!clonedElement) return;

    if (liveElement.currentSrc) {
      clonedElement.setAttribute('src', liveElement.currentSrc);
      clonedElement.removeAttribute('srcset');
      clonedElement.removeAttribute('sizes');
      clonedElement.closest('picture')?.querySelectorAll('source').forEach(source => source.remove());
    }
  });

  for (const element of Array.from(clone.querySelectorAll('*'))) {
    for (const attr of URL_ATTRIBUTES) {
      const value = element.getAttribute(attr);
      if (value) element.setAttribute(attr, resolveUrl(value));
    }
    for (const attr of SRC_ATTRIBUTES) {
      const value = element.getAttribute(attr);
      if (value) element.setAttribute(attr, resolveUrl(value));
    }
    for (const attr of SRCSET_ATTRIBUTES) {
      const value = element.getAttribute(attr);
      if (value) element.setAttribute(attr, resolveSrcset(value));
    }
  }
}

function buildCapturedHtml(doctype: string, clone: HTMLElement, runtimeCss: string): string {
  const baseTag = `<base href="${window.location.href}">`;
  const hideStyle = `<style>.annotation-dot { display: none !important; }</style>`;
  const runtimeStyle = runtimeCss
    ? `<style data-bugsmash-runtime>${runtimeCss}</style>`
    : '';
  const injection = baseTag + hideStyle + runtimeStyle;
  const html = doctype + clone.outerHTML;

  if (html.toLowerCase().includes('<head>')) {
    return html.replace(/<head>/i, `<head>${injection}`);
  }

  return doctype + injection + clone.outerHTML;
}

function buildCaptureData(
  compressedHtml: string,
  clientX: number,
  clientY: number,
): CaptureData {
  const scrollX = window.scrollX;
  const scrollY = window.scrollY;

  return {
    html: compressedHtml,
    isCompressed: true,
    boundingBox: {
      top: scrollY,
      left: scrollX,
      width: window.innerWidth,
      height: window.innerHeight,
    },
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio,
      scrollX,
      scrollY,
      aspectRatio: window.innerWidth / window.innerHeight,
    },
    screenshotEnvironment: {
      colorScheme: window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light',
    },
    // Normalized [0,1] within viewport for FeedbackImage cx/cy
    cx: clientX / window.innerWidth,
    cy: clientY / window.innerHeight,
  };
}

/**
 * Serializes the live page for the Playwright screenshot server.
 * Mirrors the capture flow in bugsmash-proxy-server/src/iframe-utils.js
 * but runs directly on the real origin (no proxy URL rewriting needed).
 *
 * @param clientX  viewport-relative X coordinate of the pin click
 * @param clientY  viewport-relative Y coordinate of the pin click
 */
export async function capturePageSection(
  clientX: number,
  clientY: number,
): Promise<CaptureData> {
  const doctype = document.doctype
    ? new XMLSerializer().serializeToString(document.doctype)
    : '<!DOCTYPE html>';

  const clone = document.documentElement.cloneNode(true) as HTMLElement;

  // Remove existing BugSmash widget root so it doesn't appear in the screenshot
  clone.querySelector('#bugsmash-widget-root')?.remove();
  normalizeClonedAssetUrls(clone);

  const runtimeCss = collectRuntimeCss();
  const html = buildCapturedHtml(doctype, clone, runtimeCss);
  const compressedHtml = await compressHtmlToBase64(html);

  return buildCaptureData(compressedHtml, clientX, clientY);
}
